export const MAX_SEARCH_QUERY_LENGTH = 120;

const DANGEROUS_SEARCH_PATTERNS = [
  /<|>|&lt;|&gt;|%3c|%3e/i,
  /`|"/,
  /\bscript\b/i,
  /javascript\s*:/i,
  /vbscript\s*:/i,
  /data\s*:/i,
  /srcdoc\s*=/i,
  /on[a-z0-9_:-]+\s*=/i,
  /expression\s*\(/i,
  /eval\s*\(/i,
  /alert\s*\(/i,
  /confirm\s*\(/i,
  /prompt\s*\(/i,
  /document\s*\./i,
  /window\s*\./i,
  /<\s*\/?\s*[a-z][^>]*>/i,
];

export function decodeRepeatedly(value) {
  let decoded = String(value || "");

  for (let index = 0; index < 3; index += 1) {
    try {
      const nextDecoded = decodeURIComponent(decoded);

      if (nextDecoded === decoded) {
        break;
      }

      decoded = nextDecoded;
    } catch (_) {
      break;
    }
  }

  return decoded;
}

export function normalizeSearchQuery(value) {
  return String(value || "")
    .split("")
    .map((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127 ? " " : character;
    })
    .join("")
    .replace(/\s+/g, " ")
    .trim();
}

export function validateSearchQuery(value) {
  const raw = String(value || "");
  const normalized = normalizeSearchQuery(raw);

  if (!normalized) {
    return {
      ok: false,
      reason: "empty",
      message: "Enter a search query.",
      value: "",
    };
  }

  if (normalized.length > MAX_SEARCH_QUERY_LENGTH) {
    return {
      ok: false,
      reason: "too_long",
      message: `Search queries must be ${MAX_SEARCH_QUERY_LENGTH} characters or fewer.`,
      value: normalized.slice(0, MAX_SEARCH_QUERY_LENGTH),
    };
  }

  const decoded = decodeRepeatedly(normalized);

  if (DANGEROUS_SEARCH_PATTERNS.some((pattern) => pattern.test(decoded))) {
    return {
      ok: false,
      reason: "dangerous_payload",
      message: "Invalid search query.",
      value: normalized,
    };
  }

  return {
    ok: true,
    reason: "valid",
    message: "",
    value: normalized,
  };
}

export function buildSearchResultsUrl(query) {
  const params = new URLSearchParams({ q: query });
  return `/search-results?${params.toString()}`;
}

export function stripSearchableHtml(value) {
  return String(value || "")
    .replace(/\[([^\]\n]{1,160})\]\((https:\/\/[^\s)]+)\)/g, "$1")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, " ")
    .trim();
}


export function getApiCandidates(apiPath) {
  const cleanPath = String(apiPath || "").startsWith("/")
    ? String(apiPath || "")
    : `/${apiPath || ""}`;

  const candidates = [cleanPath];

  if (typeof window !== "undefined") {
    const { protocol, hostname, port } = window.location;
    const isLocalFrontend =
      (hostname === "localhost" || hostname === "127.0.0.1") && port === "3000";

    if (isLocalFrontend) {
      candidates.push(`${protocol}//${hostname}:5000${cleanPath}`);
    }
  }

  return [...new Set(candidates)];
}

export async function fetchApiWithFallback(apiPath, options = {}) {
  const candidates = getApiCandidates(apiPath);
  let lastError = null;

  for (const endpoint of candidates) {
    try {
      const response = await fetch(endpoint, {
        credentials: "include",
        ...options,
      });

      if (response.ok) {
        return response;
      }

      lastError = new Error(`API request failed: ${response.status} ${endpoint}`);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("API request failed");
}

export async function fetchJsonApi(apiPath, options = {}) {
  const response = await fetchApiWithFallback(apiPath, options);
  return response.json();
}

export function logBlockedSearchPayload({ query, reason, userId = "guest", source = "search" }) {
  return fetchApiWithFallback("/api/log-event", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      type: "search_injection_blocked",
      userId,
      meta: {
        page: typeof window !== "undefined" ? window.location.pathname : "unknown",
        source,
        reason,
        queryPreview: String(query || "").slice(0, MAX_SEARCH_QUERY_LENGTH),
        browser: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      },
    }),
  }).catch((err) => console.warn("Search security log failed:", err));
}
